import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    GridItem,
    Input,
    InputGroup,
    SimpleGrid,
    Switch,
    Text,
    Tooltip,
    useDisclosure
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ValidationKey } from '@/constants/validation-key';
import { RegisterForm } from '@/models/forms/RegisterForm';

import { InternationalSponsorProps } from './InternationalSponsorProps';
import { FormControlError } from '../../FormControlError/FormControlError';

type QueryParams = {
    internationalSponsorNo?: string;
};

export const InternationalSponsor = (props: InternationalSponsorProps<RegisterForm>) => {
    const router = useRouter();
    const query = router.query as QueryParams;
    const intl = useIntl();
    const { formInstance, introductoryPerson, sponsorCheckedCallback } = props;
    const { formState, register, setValue, setError } = formInstance;
    const [isSponsorChecked, setIsSponsorChecked] = useState(false);
    const { isOpen, onOpen, onToggle, onClose } = useDisclosure();
    const [internationalSponsorNo, setInternationalSponsorNo] = useState(
        query.internationalSponsorNo
    );
    const obj = useMemo(
        () => ({
            intl,
            setError,
            setValue
        }),
        [intl, setError, setValue]
    );
    const referrerNumberControl = register('internationalSponsorNo', {
        required: {
            value: isSponsorChecked,
            message: ValidationKey.FieldIsRequired
        },
        pattern: {
            value: /^([a-zA-Z]{2})-(\d{5,6})$/,
            message: ValidationKey.SponsorNumber
        }
    });
    const sponsorEmailControl = register('internationalSponsorEmail', {
        required: {
            value: isSponsorChecked,
            message: ValidationKey.FieldIsRequired
        },
        pattern: {
            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: ValidationKey.PleaseEnterValidEmail
        }
    });

    useEffect(() => {
        sponsorCheckedCallback(!!isSponsorChecked);

        if (!isSponsorChecked) {
            obj.setValue(referrerNumberControl.name, '');
            obj.setValue(sponsorEmailControl.name, '');
            return;
        }
    }, [
        isSponsorChecked,
        referrerNumberControl.name,
        sponsorEmailControl.name,
        sponsorCheckedCallback,
        obj
    ]);

    useEffect(() => {
        if (!query.internationalSponsorNo) {
            return;
        }
        setInternationalSponsorNo(() => query.internationalSponsorNo);
        setIsSponsorChecked(() => true);
    }, [query.internationalSponsorNo]);

    return (
        <fieldset>
            <FormControl isDisabled={introductoryPerson}>
                <Flex alignItems="center">
                    <FormLabel display="flex" alignItems="center">
                        <FormattedMessage id="international-sponsor.registration.label" />
                    </FormLabel>
                    <Tooltip
                        bg="brand"
                        borderColor="grey.900"
                        hasArrow
                        isOpen={isOpen}
                        label={intl.formatMessage({
                            id: introductoryPerson
                                ? 'international-sponsor.registration.tooltip.disabled'
                                : 'international-sponsor.registration.tooltip'
                        })}
                    >
                        <InfoOutlineIcon
                            color="brand"
                            mb={2}
                            onClick={onToggle}
                            onMouseEnter={onOpen}
                            onMouseLeave={onClose}
                        />
                    </Tooltip>
                </Flex>
                <Box>
                    <Text as="span" opacity={introductoryPerson ? 0.4 : 1}>
                        <FormattedMessage id="no" />
                    </Text>
                    <Switch
                        isReadOnly={internationalSponsorNo !== undefined}
                        mx={2}
                        isChecked={isSponsorChecked}
                        onChange={() => {
                            if (!introductoryPerson) {
                                setIsSponsorChecked((prevState) => !prevState);
                            }
                        }}
                    />
                    <Text as="span" opacity={introductoryPerson ? 0.4 : 1}>
                        <FormattedMessage id="yes" />
                    </Text>
                </Box>
            </FormControl>
            {isSponsorChecked && (
                <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                    <GridItem>
                        <FormControl
                            isInvalid={!!formState.errors[referrerNumberControl.name]}
                            isRequired={isSponsorChecked}
                            isReadOnly={internationalSponsorNo !== undefined}
                        >
                            <FormLabel>
                                <FormattedMessage id="international-sponsor.label.referrer-number" />
                            </FormLabel>
                            <FormControlError formState={formState} control={referrerNumberControl}>
                                <InputGroup>
                                    <Input {...referrerNumberControl} textTransform="uppercase" />
                                </InputGroup>
                            </FormControlError>
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl
                            isInvalid={!!formState.errors[sponsorEmailControl.name]}
                            isRequired={isSponsorChecked}
                            isReadOnly={internationalSponsorNo !== undefined}
                        >
                            <FormLabel>
                                <FormattedMessage id="international-sponsor.label.e-mail" />
                            </FormLabel>
                            <FormControlError formState={formState} control={sponsorEmailControl}>
                                <Input type="email" {...sponsorEmailControl} />
                            </FormControlError>
                        </FormControl>
                    </GridItem>
                </SimpleGrid>
            )}
        </fieldset>
    );
};
